
import { Component, Vue } from 'vue-property-decorator'
import {
  getAllReferrals,
  getReferralStatistics,
  completeReferral,
  Referral,
  ReferralStats
} from '@/api/referrals'
import moment from 'moment'

@Component({
  name: 'ReferralDashboard'
})
export default class extends Vue {
  private list: Referral[] = []
  private listLoading = false
  private total = 0
  private listQuery = {
    page: 1,
    limit: 20,
    status: '',
    startDate: '',
    endDate: ''
  }

  private startDate = ''
  private endDate = ''
  private statistics: ReferralStats = {
    totalReferrals: 0,
    pendingReferrals: 0,
    completedReferrals: 0,
    rewardedReferrals: 0,
    totalBonusDaysGranted: 0
  }

  private dialogVisible = false
  private selectedReferral: Referral | null = null

  created() {
    this.loadData()
  }

  private async loadData() {
    await Promise.all([
      this.loadStatistics(),
      this.loadReferrals()
    ])
  }

  private async loadStatistics() {
    try {
      const { data } = await getReferralStatistics()
      if (data.data) {
        this.statistics = data.data
      }
    } catch (error) {
      console.error('Failed to load statistics:', error)
    }
  }

  private async loadReferrals() {
    this.listLoading = true
    try {
      const params: any = {
        page: this.listQuery.page,
        limit: this.listQuery.limit
      }

      if (this.listQuery.status) {
        params.status = this.listQuery.status
      }

      if (this.startDate) {
        params.startDate = this.startDate
      }
      if (this.endDate) {
        params.endDate = this.endDate
      }

      const { data } = await getAllReferrals(params)
      if (data.data) {
        this.list = data.data.referrals || data.data
        this.total = data.data.total || this.list.length
      }
    } catch (error) {
      console.error('Failed to load referrals:', error)
      this.$message.error('Failed to load referrals')
    } finally {
      this.listLoading = false
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.loadReferrals()
  }

  private handleSizeChange(val: number) {
    this.listQuery.limit = val
    this.loadReferrals()
  }

  private handleCurrentChange(val: number) {
    this.listQuery.page = val
    this.loadReferrals()
  }

  private handleView(row: Referral) {
    this.selectedReferral = row
    this.dialogVisible = true
  }

  private async handleComplete(row: Referral) {
    try {
      await this.$confirm(
        `Are you sure you want to manually complete this referral? This will grant bonus VA days to both the referrer and the referred user.`,
        'Complete Referral',
        {
          confirmButtonText: 'Complete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )

      await completeReferral(row.referredUserId)
      this.$message.success('Referral completed successfully')
      await this.loadData()
    } catch (error: any) {
      if (error !== 'cancel') {
        this.$message.error(error.response?.data?.message || 'Failed to complete referral')
      }
    }
  }

  private getStatusType(status: string): string {
    const statusMap: Record<string, string> = {
      pending: 'warning',
      completed: 'primary',
      rewarded: 'success',
      expired: 'info',
      cancelled: 'danger'
    }
    return statusMap[status] || ''
  }

  private formatDate(date: Date | string | null): string {
    if (!date) return 'N/A'
    return moment(date).format('DD.MM.YYYY HH:mm')
  }

  private formatPercent(value: number | undefined): string {
    if (value === undefined || value === null) return '0%'
    return `${(value * 100).toFixed(1)}%`
  }
}
